/* eslint-disable max-len */
import { getErrorIfApplies } from '../constants/errors';
import { RulesApplier } from '../rules/appliers/RuleApplier';
import { RuleSetStrategyFactory } from '../rules/factories/RuleSetStrategyFactory';
import { CartRuleSetStrategy } from '../rules/strategies/CartRuleSetStrategy';
import { MembershipRuleSetStrategy } from '../rules/strategies/MembershipRuleSetStrategy';
import { RuleContext } from '../rules/types/RuleContext';
import { ErrorValidating } from '../types/cart';
import { CouponDetail } from '../types/cupom';
import { Event } from '../types/event';
import { Membership, MembershipDetail, MembershipRuleValue } from '../types/membership';
import { Ticket, TicketType } from '../types/ticket';
import { useI18nContext } from './contexts/i18n.context';
import { ValidationContext } from './contexts/membership/validationContext';
type RuleProps = {
  event?: Event;
  couponApplied: CouponDetail | null;
  tickets: Ticket[];
};

type BffExtraKeysProps = {
  passports: Ticket[], singleDays: Ticket[]
}

type SelectionRule = (props: ApplySelectionTicketRulesProps) => ReturnSelection

type ApplySelectionRulesProps = {
  event: Event;
  tickets: Ticket[];
  couponApplied: CouponDetail | null;
  updateTicketList: (tickets: Ticket[], event: Event) => void;
  updateMembershipError?: (error: MembershipRuleValue | null) => void;
  membership?: Membership;
};

interface ApplyCartRulesProps {
  event: Event;
  tickets: Ticket[];
  cart: Ticket[];
  couponApplied: CouponDetail | null;
  passKeyApplied: string | null;
  updateCart: (tickets: Ticket[]) => void;
  updateSummaryData: (params: { newCart: Ticket[] }) => void;
  membership?: Membership | null;
  updateTicketsPerSession?: (definitiveSessionId: string, quantity: number) => void;
}

interface ApplyMembershipValidationRulesProps {
  tickets?: Ticket[];
  membership: Membership | null;
  membershipResponse: MembershipDetail | null;
}

type ApplySelectionTicketRulesProps = {
  ticketId: number;
  groupId: number;
  clickedTicketId: number;
  cart: Ticket[];
  event: Event | null;
  tickets: Ticket[];
  membershipApplied: Membership | null;
}

type ReturnSelection = {
  disableAddToCart: boolean;
  errorMessage: string | null
}

type AddExtraKeyToTicketProps<T> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: T, key: string, value: any
}

/**
 * Custom hook that manages ticket selection rules.
*/
const useRules = () => {
  const { translate } = useI18nContext();

  /**
   * Adds an extra key to an object.
   * This function takes an object and adds a new key-value pair to it. The original object remains unchanged,
   * and a new object with the added key-value pair is returned.
   * @template T - The type of the input object.
   * @param {Object} params - The parameters for the function.
   * @param {T} params.data - The object to which the extra key will be added.
   * @param {string} params.key - The key to be added.
   * @param {any} params.value - The value of the key to be added.
   * @returns The new object with the extra key added.
  */
  function addExtraKeyToTicket<T>({ data, key, value }: AddExtraKeyToTicketProps<T>) {
    return { ...data, [key] : value };
  }

  /**
   * Adds extra keys to a ticket object.
   * This function takes a `TicketType` object and adds several extra keys to it, including `tagType`, `originalPrice`,
   * `accumulatedValueInCart`. The original `ticket` object is spread into a new object
   * along with the added keys and their corresponding values.
   * @param {TicketType} ticket - The ticket object to which extra keys will be added.
   * @returns TicketType A new `TicketType` object with the extra keys added.
  */
  const addExtraKeysToTickets = (ticket: TicketType): TicketType => {
    return {
      ...ticket,
      ...addExtraKeyToTicket<TicketType>({ data : ticket, key : 'tagType', value : undefined }),
      ...addExtraKeyToTicket<TicketType>({ data : ticket, key : 'idCarouselRef', value : 0 }),
      ...addExtraKeyToTicket<TicketType>({ data : ticket, key : 'idSpecificInIdentifyList', value : undefined }),
      ...addExtraKeyToTicket<TicketType>({ data : ticket, key : 'checkoutPrice', value : [] }),
      ...addExtraKeyToTicket<TicketType>({ data : ticket, key : 'originalPrice', value : ticket.price }),
      ...addExtraKeyToTicket<TicketType>({ data : ticket, key : 'accumulatedValueInCart', value : 0 }),
    };
  };

  /**
   * Adds extra keys to single day and passport ticket lists.
   * This function processes two lists of tickets, one for passports and the other for single days.
   * For each passport ticket, add the key `isPassport` with the value `true`.
   * For each single day ticket, add the key `isPassport` with the value `false`.
   * It then adds several extra keys to each ticket type within these lists.
   * @param {Object} params - The parameters for the function.
   * @param {Ticket[]} params.passports - The list of passport tickets.
   * @param {Ticket[]} params.singleDays - The list of single day tickets.
   * @returns A new list of tickets with the extra keys added.
  */
  function bffAddExtraKeysToTicketListing({ passports, singleDays }: BffExtraKeysProps): Ticket[] {

    const passportsWithExtraKeys = passports.map((passport) =>
      addExtraKeyToTicket({ data : passport, key : 'isPassport', value : true }));

    const singleDaysWithExtraKeys = singleDays.map((singleDay) =>
      addExtraKeyToTicket({ data : singleDay, key : 'isPassport', value : false }));

    return [...passportsWithExtraKeys, ...singleDaysWithExtraKeys].map((ticket) => ({
      ...ticket, type : ticket.type.map((type) => (addExtraKeysToTickets(type)))
    }));
  }

  /**
   * Determines the status of a ticket and returns the corresponding translated message.
   * @param {Object} param - The parameter object.
   * @param {TicketType} param.ticket - The ticket type to check.
  */
  const ticketStatus = ({ ticket }: { ticket: TicketType }) => {
    if (ticket.status === 'soldout')
      return translate(['INFORMATION', 'SOLD_OFF']);

    if (ticket.status !== 'available')
      return translate(['INFORMATION', 'SALES_ENDED']);

    return '';
  };

  /**
   * Determines the status of a ticket and returns the corresponding translated message.
   * @param {Object} param - The parameter object.
   * @param {TicketType} param.ticket - The ticket type to check.
  */
  const sessionStatus = ({ ticket }: { ticket: TicketType }) => {
    if (ticket.status === 'finished')
      return { canSell : false, message : translate(['INFORMATION', 'SALES_ENDED']) };

    if (ticket.status === 'soldout' || ticket.quantityInStock === 0)
      return { canSell : false, message : translate(['INFORMATION', 'SOLD_OFF']) };

    if (ticket.status !== 'available')
      return { canSell : false, message : translate(['INFORMATION', 'SALES_ENDED']) };

    return { canSell : true, message : '' };
  };

  const ticketGroupStatus = ({ ticket }: { ticket: Ticket }) => {
    if (ticket.status === 'soldout') {
      return translate(['INFORMATION', 'SOLD_OFF']);
    }
    return translate(['INFORMATION', 'SALES_ENDED']);
  };

  /**
   * Checks if all ticket types in a ticket are sold out.
   * @param ticket - The ticket to check.
   * @returns True if all ticket types are sold out, otherwise false.
  */
  const checksIfAllTicketsAreSoldOut = ({ ticket }: { ticket: Ticket }) => {
    return ticket.type.every(({ quantityInStock }) => quantityInStock === 0);
  };

  /**
   * Checks if any ticket type in a ticket can be sold.
   * @param ticket - The ticket to check.
   * @returns True if any ticket type can be sold, otherwise false.
  */
  const hasSellableTicketType = (ticket: Ticket) => {
    return ticket.type.some(({ quantityInStock }) => quantityInStock > 0);
  };

  /**
   * Checks if all tickets are unavailable (finished).
   * @param tickets - The array of tickets to check.
   * @returns True if all tickets are finished, otherwise false.
  */
  const checkIfAllTicketsAreUnavailable = ({ tickets, event }: { tickets: Ticket[], event: Event }) => {
    let ticketsWithSessionsHandled = [...tickets];

    if (event?.attributes.sessionHidePast.value) {
      ticketsWithSessionsHandled = tickets.filter((ticketGroup) => (
        !ticketGroup.type.every((ticket) => (
          ticket.dates.every((date) => date.status === 'finished'))
        )
      ));
    }

    return !ticketsWithSessionsHandled.some((ticket) => !ticket.finished);
  };

  /**
   * Checks if any membership ticket type in a ticket group is enabled.
   * @param ticket - The ticket to check.
   * @returns True if any ticket type can be sold, otherwise false.
  */
  const checkAnyMembershipEnabledInTheGroup = ({ ticket }: { ticket: Ticket }) => {
    return ticket.type.some((type) => type.status === 'available');
  };

  /**
   * Applies a discount coupon to the available tickets.
   * @param {Object} params - The function parameters.
   * @param {Ticket[]} params.tickets - The list of available tickets.
   * @param {Coupon | null} params.couponApplied - The applied coupon or null if no coupon is applied.
   * @returns {Ticket[]} - The updated list of tickets with the discounts applied.
   *
   * This function iterates over the list of tickets and applies the specified discount
   * to each ticket whose ID matches one of the tickets associated with the coupon.
   * The discount can be either a percentage or a fixed value, and is subtracted from the
   * original price of the ticket. The function returns the updated list of tickets with
   * the discounted prices and the coupon information added to the `tagType` field.
  */
  const applyCoupon = ({ tickets, couponApplied }: RuleProps): Ticket[] => {
    if (!couponApplied) return tickets;

    return tickets.map(fullTicket => ({
      ...fullTicket,
      type : fullTicket.type.map(onlyTicket => {
        if (!onlyTicket.hasCoupon || !onlyTicket.coupon) return onlyTicket;

        const isPercentual = onlyTicket.coupon.type_discount === 'percentual';

        const discountValue = isPercentual ? onlyTicket.originalPrice * (onlyTicket.coupon.value_discount / 100) : onlyTicket.coupon.value_discount;
        const discountedPrice = Math.max(onlyTicket.originalPrice - discountValue, 0);

        return {
          ...onlyTicket,
          price : discountedPrice,
          tagType : {
            coupon : {
              value : onlyTicket.coupon.value_discount,
              typeDiscount : onlyTicket.coupon.type_discount
            }
          }
        };
      })
    }));
  };

  const thisTicketHasAnError = ({ ticket, error }: { ticket: TicketType, error: ErrorValidating }) => {
    if (!error.code || !error.error) return false;
    return getErrorIfApplies({ errorCode : error.code }).some((category) => String(category) === ticket.category.id);
  };

  /**
   * Applies a limit reached rule for tickets based on the quantity in cart and the stock available.
   * Updates each ticket type with an appropriate limit error message if the limit is reached.
   *
   * @param tickets - The array of tickets to update.
   * @returns The updated array of tickets with the limitErrorMessage status.
  */
  const applyDefaultReachedRule = ({ ticketId, cart, event }: ApplySelectionTicketRulesProps): ReturnSelection => {
    let result: ReturnSelection = { disableAddToCart : false, errorMessage : null };

    let totalTickets = 0;

    cart.map((group) => {
      group.type.map((type) => {
        totalTickets += type.quantityInCart;
      });
    });
    if (event?.attributes.cpfTicketsLimit.value) {
      const containsMoreThanMax = totalTickets >= (event?.attributes?.cpfTicketsLimit.value);
      if (containsMoreThanMax) {
        return { disableAddToCart : true, errorMessage : null };
      }
    }

    cart.forEach((ticket) => {
      ticket.type.forEach((type) => {
        const validations = [
          {
            condition : type.restrictions.maximum === type.restrictions.minimum,
            message : translate(['INFORMATION', 'LIMIT_AUTOMATIC']),
            disable : true,
          },
          {
            condition : type.quantityInCart >= type.quantityInStock,
            message : translate(['INFORMATION', 'LIMIT_MAX_TICKET']),
            disable : true,
          },
          {
            condition : type.quantityInCart >= type.restrictions.maximum,
            message : translate(['INFORMATION', 'LIMIT_REACHED']),
            disable : true,
          },
          {
            condition : type.quantityInCart < type.restrictions.minimum,
            message :
              translate(['INFORMATION', 'MIN_LIMIT']) +
              type.restrictions.minimum +
              translate(['INFORMATION', 'TYPE_TICKET']),
            disable : false,
          },
        ];
        if (type.id === ticketId) {
          const matchedValidation = validations.find((v) => v.condition);

          result = {
            disableAddToCart : matchedValidation?.disable || false,
            errorMessage : matchedValidation?.message || null,
          };
        }
      });
    });

    return result;
  };

  /**
   * Applies membership rules to the cart.
   * Rules:
   * - Given that the user enters a CPF in the membership field.
   * - When the CPF is active.
   * - Then the membership limit is one per CPF.
   * @function applyRulesMembership
   * @param {Object} params - The parameters for applying membership rules.
   * @param {Array} params.cart - The cart containing the tickets.
   * @param {boolean} params.membershipApplied - Flag indicating if the membership is applied.
   * @param {number} params.ticketId - The ID of the ticket to check.
   * @param {number} params.groupId - The ID of the group to which the ticket belongs.
   * @returns {Object} The result indicating if the limit is exceeded and an error message if applicable.
  */
  const applyRulesMembership = (props: ApplySelectionTicketRulesProps): ReturnSelection => {
    const { cart, membershipApplied, ticketId, groupId } = props;
    const result: ReturnSelection = { disableAddToCart : false, errorMessage : null };

    const groupInCartList = cart.find((group) => group.id === groupId);

    if (!membershipApplied || !groupInCartList) return result;

    const ticketInCart = groupInCartList.type.find((ticket) => ticket.id === ticketId);

    if (!ticketInCart) return result;

    const exceeded = ticketInCart.quantityInCart >= 1;

    return {
      disableAddToCart : exceeded,
      errorMessage : exceeded ? translate(['INFORMATION', 'LIMIT_REACHED']) : null
    };

  };

  /**
   * Applies rules to block the addition of tickets benefit from more than one to the cart.
   * Given that the user has fan member and companion tickets:
   * - When the user tries to add tickets to the cart,
   * - Then companion tickets must be added in the same group as the fan member tickets.
   * @param {ApplySelectionTicketRulesProps} props - The properties for applying the selection rules.
   * @param {TicketGroup[]} props.tickets - The list of available tickets.
   * @param {TicketGroup[]} props.cart - The current cart containing selected tickets.
   * @param {number} props.ticketId - The ID of the ticket being added.
   * @param {number} props.groupId - The ID of the group to which the ticket being added belongs.
   * @returns {ReturnSelection} The result indicating whether to disable adding the ticket to the cart and any error message.
  */
  const blockTicketBenefitWhenAlreadyHaveAndMax = (props: ApplySelectionTicketRulesProps): ReturnSelection => {
    const { tickets, cart, groupId, ticketId, event, membershipApplied } = props;

    let totalTickets = 0;

    cart.map((group) => {
      group.type.map((type) => {
        totalTickets += type.quantityInCart;
      });
    });
    const validationContext = new ValidationContext();
    const result = validationContext.validate(membershipApplied, cart, event, tickets, groupId, ticketId, totalTickets);
    return result;
  };

  /**
   * Applies a set of ticket selection rules to update the ticket list.
   * @param props - The properties for applying the ticket selection rules.
  */
  const applySelectionTicketRules = (props: ApplySelectionTicketRulesProps): ReturnSelection => {
    const { ticketId, groupId, cart, tickets, event, membershipApplied, clickedTicketId } = props;

    const defaultRulesForAllEvents: SelectionRule[] = [applyDefaultReachedRule];

    const listOfSpecificRules: Record<string, SelectionRule[]> = {
      Futebol : [
        applyRulesMembership,
        blockTicketBenefitWhenAlreadyHaveAndMax,
      ]
    };

    let result: ReturnSelection = { disableAddToCart : false, errorMessage : null };

    if (!event || cart.length === 0) return result;

    defaultRulesForAllEvents.forEach((rule) => {
      result = rule({ cart, event, ticketId, groupId, membershipApplied, tickets, clickedTicketId });
    });

    const deniedProducers = [5499207, 5259080, 6166773, 6451456, 6084768, 809191];
    event.categories.forEach((category) => {
      if (deniedProducers.includes(event.producerId)) return;
      const specificRules = listOfSpecificRules[category.name] || [];
      specificRules.forEach((rule) => {
        result = rule({ cart, event, ticketId, groupId, membershipApplied, tickets, clickedTicketId });
      });
    });

    return result;
  };

  /**
   * Applies a set of ticket selection rules to update the ticket list.
   * @param props - The properties for applying the ticket selection rules.
  */
  const applyTicketRules = (props: ApplySelectionRulesProps) => {
    const { event, tickets, couponApplied, updateTicketList : updateTicket, membership, updateMembershipError } = props;

    const context: Partial<RuleContext> = { event, tickets, couponApplied, membership, updateMembershipError };
    const strategy = RuleSetStrategyFactory.getStrategy(context);
    const rulesApplier = new RulesApplier(strategy);

    const updatedTickets = rulesApplier.apply(context);

    updateTicket(updatedTickets, event);
  };

  const applyCartRules = (props: ApplyCartRulesProps) => {
    const { event, tickets, cart, couponApplied, passKeyApplied, updateCart, updateSummaryData, membership, updateTicketsPerSession } = props;

    const context: Partial<RuleContext> = { event, tickets, cart, couponApplied, passKeyApplied, membership, updateTicketsPerSession };
    const strategy = new CartRuleSetStrategy();
    const rulesApplier = new RulesApplier(strategy);

    const updatedTickets = rulesApplier.apply(context);

    updateCart(updatedTickets);
    updateSummaryData({ newCart : updatedTickets });
  };

  const applyMembershipValidationRules = (props: ApplyMembershipValidationRulesProps) => {
    const { tickets, membership, membershipResponse = null } = props;

    const context: Partial<RuleContext> = { tickets, membership, membershipResponse };
    const strategy = new MembershipRuleSetStrategy();
    const ticketApplier = new RulesApplier<MembershipRuleValue>(strategy);

    const ticketRules = ticketApplier.apply(context);
    return ticketRules;
  };

  return {
    ticketGroupStatus,
    applyCoupon,
    ticketStatus,
    sessionStatus,
    thisTicketHasAnError,
    addExtraKeysToTickets,
    hasSellableTicketType,
    applyTicketRules,
    applyCartRules,
    applySelectionTicketRules,
    checksIfAllTicketsAreSoldOut,
    bffAddExtraKeysToTicketListing,
    checkIfAllTicketsAreUnavailable,
    checkAnyMembershipEnabledInTheGroup,
    blockTicketBenefitWhenAlreadyHaveAndMax,
    applyMembershipValidationRules,
  };
};

export default useRules;
